/**
 * Row reducer (and its validation helpers) for the CPT table's
 * "Auth Required" (PA-required status) cell.
 *
 * A status change here cascades into a full revalidation of every
 * auth-sensitive field plus J-code and document-icon post-processing.
 * @module updateRowData/paRequiredReducer
 */
import moment from "moment";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../../data-model/medicalPaOrdersModel";
import {
  isFacilityExemptFromDateFromValidation,
  isMedOncOrRadiologyOrg,
} from "../../../../../../enums/organizationTypes";
import { CptCodeWithDetailsKeys } from "../../../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../../../utils/formatDate";
import { getPendingNumberEditable } from "../../../../../../utils/getPendingNumberEditable";
import {
  checkIfAuthNumberFieldHasError,
  checkIfDateIsAfter,
  checkIfDateIsEarlierThanDateOfService,
  checkIfDateIsInCorrectFormat,
  checkIfDateIsLaterThanDateOfService,
  checkIfNumVisitsIsBlank,
  checkIfNumVisitsMatchesRemainingAndCompleted,
  checkIfStringIsValidNumber,
} from "../computeErrorStates";
import {
  determineIfTheAuthNumberFieldShouldBeEditable,
  doesAuthStatusAllowEmptyAuthNumber,
} from "../determineIfTheFieldShouldBeEditable";
import { doModificationsRelatedToJCode } from "../doModificationsRelatedToJCode";
import { updateRowDataForDocWithUploadIcon } from "../updateRowDataForDocWithUploadIcon";
import { recomputeFilingInfo } from "./filingInfoReducer";
import {
  isStatusRequiringValidation,
  updateFieldWithError,
} from "./sharedHelpers";

/**
 * Computes per-field validation error flags for all auth-sensitive fields
 * (start date, end date, remaining visits, visits allowed, total visits,
 * authorization number).
 *
 * Only performs validation when the auth status requires it; otherwise
 * every field maps to `false` (no error).
 *
 * Facility-specific rules:
 * - Facilities exempted by {@link isFacilityExemptFromDateFromValidation}
 *   (radiology and urology categories) skip the `StartFrom` validation entirely.
 * - `EndAt` must be a valid date, later than the date of service, AND
 *   strictly after `StartFrom`.
 *
 * @param rowData - The current row data containing each field's value.
 * @param dateOfService - The order's date of service used for date comparisons.
 * @param requiresValidation - Whether the current status demands validation.
 * @param category - Healthcare facility `category` used to apply
 *   facility-specific validation exceptions.
 * @param authStatusId - The new PA-required status identifier.
 * @returns A record mapping each validated `CptCodeWithDetailsKeys` to its error state.
 */
const computeAuthFieldErrors = (
  rowData: any,
  dateOfService: string,
  requiresValidation: boolean,
  category: string | null | undefined,
  authStatusId: string,
): Record<string, boolean> => {
  const formattedDateOfService = formatDateToUTCWithFormat(
    dateOfService,
    "MM/DD/YYYY",
    moment.utc().format("MM/DD/YYYY"),
  );
  if (!requiresValidation) {
    return {
      [CptCodeWithDetailsKeys.StartFrom]: false,
      [CptCodeWithDetailsKeys.EndAt]: false,
      [CptCodeWithDetailsKeys.RemainingVisits]: false,
      [CptCodeWithDetailsKeys.VisitsAllowed]: false,
      [CptCodeWithDetailsKeys.Visits]: false,
      [CptCodeWithDetailsKeys.AuthorizationNumber]: false,
    };
  }

  const shouldShowDateFromError =
    !isFacilityExemptFromDateFromValidation(category);

  const startDate = rowData[CptCodeWithDetailsKeys.StartFrom]?.data ?? "";
  const endDate = rowData[CptCodeWithDetailsKeys.EndAt]?.data ?? "";
  const remainingVisits =
    rowData[CptCodeWithDetailsKeys.RemainingVisits]?.data ?? "0";
  const visitsAllowed =
    rowData[CptCodeWithDetailsKeys.VisitsAllowed]?.data ?? "0";
  const visits = rowData[CptCodeWithDetailsKeys.Visits]?.data ?? "0";
  const authorizationNumber =
    rowData[CptCodeWithDetailsKeys.AuthorizationNumber]?.data ?? "";

  return {
    [CptCodeWithDetailsKeys.StartFrom]: shouldShowDateFromError
      ? !checkIfDateIsInCorrectFormat(startDate, "MM/DD/YYYY") ||
        !checkIfDateIsEarlierThanDateOfService(
          startDate,
          formattedDateOfService,
        )
      : false,

    [CptCodeWithDetailsKeys.EndAt]:
      !checkIfDateIsInCorrectFormat(endDate, "MM/DD/YYYY") ||
      !checkIfDateIsLaterThanDateOfService(endDate, formattedDateOfService) ||
      !checkIfDateIsAfter(endDate, startDate),

    [CptCodeWithDetailsKeys.RemainingVisits]:
      !checkIfStringIsValidNumber(remainingVisits),

    [CptCodeWithDetailsKeys.VisitsAllowed]:
      !checkIfStringIsValidNumber(visitsAllowed),

    [CptCodeWithDetailsKeys.Visits]:
      !checkIfStringIsValidNumber(visits) ||
      checkIfNumVisitsIsBlank(parseInt(visits)) ||
      !checkIfNumVisitsMatchesRemainingAndCompleted(
        parseInt(visits),
        parseInt(remainingVisits),
        parseInt(visitsAllowed),
      ),
    [CptCodeWithDetailsKeys.AuthorizationNumber]:
      checkIfAuthNumberFieldHasError(authorizationNumber, authStatusId),
  };
};

/** The set of fields that are re-validated when the PA-required status changes. */
const VALIDATION_SENSITIVE_FIELDS = [
  CptCodeWithDetailsKeys.StartFrom,
  CptCodeWithDetailsKeys.EndAt,
  CptCodeWithDetailsKeys.RemainingVisits,
  CptCodeWithDetailsKeys.VisitsAllowed,
  CptCodeWithDetailsKeys.Visits,
  CptCodeWithDetailsKeys.AuthorizationNumber,
] as const;

/**
 * Builds an object of updated field values with their recalculated error flags.
 *
 * @param rowData - The current row data.
 * @param errorStates - Pre-computed error flags keyed by field name.
 * @returns A partial row update containing each validated field with its error state.
 */
const buildFieldsWithErrors = (
  rowData: any,
  errorStates: Record<string, boolean>,
): Record<string, any> =>
  VALIDATION_SENSITIVE_FIELDS.reduce(
    (acc, fieldKey) => {
      acc[fieldKey] = updateFieldWithError(
        rowData,
        fieldKey,
        errorStates[fieldKey],
      );
      return acc;
    },
    {} as Record<string, any>,
  );

/**
 * Applies J-code modifications and document-upload-icon updates to a row.
 *
 * This is the post-processing pipeline that must run after the PA-required
 * status and field errors have been merged into the row data.
 *
 * @param rowData - The row data with PA status and error states already applied.
 * @param authOnFileDetails - The auth-on-file payload, used for document-upload-icon resolution.
 * @returns The fully post-processed row data.
 */
const applyJCodeAndDocModifications = (
  rowData: any,
  authOnFileDetails: AuthOnFile,
) => {
  const withJCodeMods = doModificationsRelatedToJCode(rowData);
  return updateRowDataForDocWithUploadIcon(withJCodeMods, authOnFileDetails);
};

/**
 * Recomputes the authorization-number cell's editability from the current
 * PA-required status.
 *
 * ``allowEmptyValue`` follows {@link doesAuthStatusAllowEmptyAuthNumber} so an
 * empty Auth# does not show a red border for those statuses (matches the
 * initial table generation in ``generateCptCodesTableData``).
 *
 * @param rowData - The current row data.
 * @returns The row data with `AuthorizationNumber.allowInputModification` updated.
 */
const determineIfAuthNumberShouldBeEditable = (rowData: any) => {
  const authStatusId =
    rowData[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ?? "";
  const isEditable =
    determineIfTheAuthNumberFieldShouldBeEditable(authStatusId);
  return {
    ...rowData,
    [CptCodeWithDetailsKeys.AuthorizationNumber]: {
      ...rowData[CptCodeWithDetailsKeys.AuthorizationNumber],
      allowInputModification: isEditable,
      allowEmptyValue: doesAuthStatusAllowEmptyAuthNumber(authStatusId),
    },
  };
};

/**
 * Recomputes the pending-number cell's editability from the prior and new
 * PA-required statuses.
 *
 * Pending# stays locked unless the transition is a direct flip from
 * `auth_required` to one of the whitelisted processing/denial statuses.
 * Empty values are always allowed.
 *
 * @param rowData - The current row data
 * @param oldAuthStatusId - PA-required status before this edit
 * @param newAuthStatusId - PA-required status after this edit
 * @return The row data with `PendingNumber.allowInputModification` updated
 */
const determineIfPendingNumberShouldBeEditable = (
  rowData: any,
  oldAuthStatusId: string | undefined,
  newAuthStatusId: string | undefined,
) => ({
  ...rowData,
  [CptCodeWithDetailsKeys.PendingNumber]: {
    ...rowData[CptCodeWithDetailsKeys.PendingNumber],
    allowInputModification: getPendingNumberEditable(
      oldAuthStatusId,
      newAuthStatusId,
    ),
    allowEmptyValue: true,
    hasError: false,
  },
});

/**
 * Handles a PA-required status change by revalidating all auth-sensitive
 * fields, recomputing Filing Info, applying J-code modifications, and
 * updating document upload icons.
 *
 * When the new status is `AuthOnFile` or `AuthByRISA`, every date and visit
 * field is validated against the order's date of service and internal
 * consistency rules. For other statuses, all error flags are cleared.
 * Filing Info is refreshed from the previous and new `currentStatusId` via
 * {@link recomputeFilingInfo}.
 *
 * @param rowDataToUpdate - The current CPT-code row data.
 * @param updatedData - The new PA-required cell payload (includes `currentStatusId`).
 * @param generalOrderDetails - The parent order context (date of service).
 * @param authOnFileDetails - The auth-on-file payload for document post-processing.
 * @param category - Healthcare facility `category` driving the start-date exemption.
 * @returns The fully updated row data with recalculated errors and post-processing applied.
 */
export const updateRowDataForPaRequired = (
  rowDataToUpdate: any,
  updatedData: any,
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  authOnFileDetails: AuthOnFile | null | undefined,
  category?: string | null,
) => {
  const requiresValidation = isStatusRequiringValidation(
    updatedData?.currentStatusId,
  );

  const errorStates = computeAuthFieldErrors(
    rowDataToUpdate,
    generalOrderDetails?.payload?.date_of_service ?? "",
    requiresValidation,
    category,
    updatedData?.currentStatusId ?? "",
  );

  const validatedFields = buildFieldsWithErrors(rowDataToUpdate, errorStates);

  const mergedRowData = {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.PaRequired]: { ...updatedData },
    ...validatedFields,
  };
  const withAuthNumberMods =
    determineIfAuthNumberShouldBeEditable(mergedRowData);
  const withPendingNumberMods = determineIfPendingNumberShouldBeEditable(
    withAuthNumberMods,
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId,
    updatedData?.currentStatusId,
  );
  // Filing Medium only exists for MedOnc/Radiology facilities; skip the
  // recompute for every other org so no filing cell state is touched.
  const withFilingInfo = isMedOncOrRadiologyOrg(category)
    ? recomputeFilingInfo(
        withPendingNumberMods,
        rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId,
        updatedData?.currentStatusId,
      )
    : withPendingNumberMods;
  return applyJCodeAndDocModifications(withFilingInfo, authOnFileDetails ?? {});
};
